import { type ReactNode, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { BarChart3, Edit, Trash } from "lucide-react"
import { useNavigate } from "react-router"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { PermissionButton } from "@/components/Auth"
import { usePermissionCheck, useToast } from "@/hooks"
import { useDeleteAgentApi } from "@/hooks/api"
import { Agent, AgentType, Permission, Workspace } from "@/types"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import DeleteDialog from "./DeleteDialog"

export interface AgentInlineActionsProps {
  className?: string
  workspace: Workspace
  agent: Agent
  onPlayground?: (agent: Agent) => void
  onDelete?: (agent: Agent) => void
}

const AgentInlineActions = ({
  className,
  workspace,
  agent,
  onPlayground,
  onDelete,
}: AgentInlineActionsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { alertToast, successToast } = useToast()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const {
    error: deleteError,
    data: deleteRespData,
    fetchFn: deleteFn,
  } = useDeleteAgentApi(agent.id)

  useEffect(() => {
    if (!deleteError) return
    console.error("Failed to delete an agent", deleteError)

    alertToast({
      title: t("workspace.agent.message.deleteFailed.title"),
      description: getUserFacingApiErrorMessage(
        deleteError,
        t,
        deleteRespData?.message ||
          t("workspace.agent.message.deleteFailed.title")
      ),
    })
  }, [deleteError, deleteRespData, alertToast, t])

  const handleDeleteAgent = () => {
    deleteFn({
      callbackFn: (resp) => {
        if (!resp) return

        successToast({
          title: t("workspace.actionButton.messages.deleted.title"),
          description: t(
            "workspace.actionButton.messages.deleted.description",
            {
              name: ellipsis(agent.name, 54),
            }
          ),
        })
        onDelete?.(agent)
      },
    })
  }

  const handleOpenEvaluation = () => {
    navigate(
      `/workspaces/${agent.workspaceId ?? workspace.id}/agents/${
        agent.id
      }?tab=evaluation`
    )
  }

  return (
    <>
      <div className={`flex items-center gap-1 ${className ?? ""}`}>
        <AgentInlineIconButton
          label={t("workspace.agent.actions.playground")}
          disabledTooltip={t("common.noPermission")}
          workspace={workspace}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          onClick={() => onPlayground?.(agent)}
        >
          <Edit className="h-4 w-4" />
        </AgentInlineIconButton>
        {agent.type === AgentType.Basic && (
          <AgentInlineIconButton
            label={t("workspace.agent.actions.evaluate")}
            disabledTooltip={t("common.noPermission")}
            workspace={workspace}
            className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
            onClick={handleOpenEvaluation}
          >
            <BarChart3 className="h-4 w-4" />
          </AgentInlineIconButton>
        )}
        <AgentInlineIconButton
          label={t("workspace.agent.actions.delete")}
          disabledTooltip={t("common.noPermission")}
          workspace={workspace}
          className="text-muted-foreground hover:text-red-600"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash className="h-4 w-4" />
        </AgentInlineIconButton>
      </div>

      <DeleteDialog
        agent={agent}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteAgent}
      />
    </>
  )
}

interface AgentInlineIconButtonProps {
  label: string
  disabledTooltip: string
  workspace: Workspace
  children: ReactNode
  className?: string
  onClick?: () => void
}

const AgentInlineIconButton = ({
  label,
  disabledTooltip,
  workspace,
  children,
  className,
  onClick,
}: AgentInlineIconButtonProps) => {
  const { hasPermission } = usePermissionCheck({
    permission: Permission.WsUpdateResources,
    workspace,
  })
  const button = (
    <span className="inline-flex">
      <PermissionButton
        permission={Permission.WsUpdateResources}
        workspace={workspace}
        disabledTooltip={disabledTooltip}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={`h-8 w-8 text-muted-foreground hover:text-foreground ${className ?? ""}`}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
      >
        {children}
      </PermissionButton>
    </span>
  )

  if (!hasPermission) {
    return button
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export { AgentInlineActions }
export default AgentInlineActions
